import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import TextAreaDisplay from 'components/form/Inputs/TextAreaDisplay';
import ArrowRightIcon from 'components/icons/ArrowRight';
import Space from 'components/Space';
import { MentorshipStatusValues } from 'enums/MentorshipStatus';
import { FC, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { mutate } from 'swr';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate } from 'utils/time';
import { ButtonsContainer, InputContainer } from '.';
import { MentoringContext } from '..';
import ChangeReferee from './ChangeReferee';

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  & > * {
    margin-bottom: 1rem;
  }
  margin-bottom: 1rem;
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InterruptionRequest: FC = () => {
  const { mentorship } = useContext(MentoringContext);
  const [interruptLoading, setInterruptLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const handleInterrupt = () => {
    setInterruptLoading(true);
    setError(false);
    privateAPI
      .put(`/mentorship/${mentorship?.id}`, {
        status: 'INTERRUPTED_ADMIN' as MentorshipStatusValues,
      })
      .then(() => {
        mutate(`/mentorship/mentee/${mentorship!.menteeId}`);
        mutate(`/mentorship/mentee/${mentorship!.menteeId}/ongoing`);
      })
      .catch(() => {
        setError(false);
        setInterruptLoading(false);
      });
  };

  const handleFinish = () => {
    setFinishLoading(true);
    setError(false);
    privateAPI
      .put(`/mentorship/${mentorship?.id}`, {
        status: 'FINISHED_ADMIN' as MentorshipStatusValues,
      })
      .then(() => {
        mutate(`/mentorship/mentee/${mentorship!.menteeId}`);
        mutate(`/mentorship/mentee/${mentorship!.menteeId}/ongoing`);
      })
      .catch(() => {
        setError(false);
        setFinishLoading(false);
      });
  };

  const handleContinue = () => {
    setContinueLoading(true);
    setError(false);
    privateAPI
      .post(`/mentorship/continue-mentorship/${mentorship!.id}`)
      .then(() => {
        mutate(`/mentorship/mentee/${mentorship!.menteeId}`);
        mutate(`/mentorship/mentee/${mentorship!.menteeId}/ongoing`);
      })
      .catch(() => {
        setError(false);
        setContinueLoading(false);
      });
  };

  return (
    <div>
      <ActionContainer>
        <p>
          Le{' '}
          {mentorship?.status === 'INTERRUPTION_REQUEST_MENTEE'
            ? 'mentoré '
            : 'mentor'}{' '}
          a demandé l'interruption du mentorat{' '}
          {mentorship?.interruptionMotive ? (
            <>
              avec le motif suivant:
              <Space height={1} />
              <i>
                <TextAreaDisplay text={mentorship.interruptionMotive} />
              </i>
            </>
          ) : (
            'sans donner de motif.'
          )}
        </p>
        <Space height={1} />
        <p>Quelle action souhaitez-vous effectuer ?</p>
        <ButtonsContainer>
          <Button
            color="primary"
            onClick={handleInterrupt}
            loading={interruptLoading}
          >
            Interrompre le mentorat
          </Button>
          <Button
            color="primary"
            onClick={handleFinish}
            loading={finishLoading}
          >
            Terminer le mentorat
          </Button>
          <Button
            color="primary"
            loading={continueLoading}
            onClick={handleContinue}
          >
            Continuer le mentorat
          </Button>
        </ButtonsContainer>
        {error && <Error>Une erreur est survenue.</Error>}
      </ActionContainer>
      <Info>
        <InputContainer>
          <Input
            value={mentorship?.createdAt && getLocaleDate(mentorship.createdAt)}
            readOnly
            variant="disabled"
            label="Date du mentorat"
          />
        </InputContainer>
        <InputContainer>
          <Input
            value={
              mentorship?.interviews?.length && mentorship.interviews[0].date
                ? getLocaleDate(mentorship.interviews[0].date)
                : "Aucun entretien n'a eu lieu"
            }
            readOnly
            variant="disabled"
            label="Date du dernier entretien"
          />
        </InputContainer>
        <InputContainer>
          <p>Mentor actuel</p>
          <Link
            to={`/admin/mentoring/mentors/${mentorship?.mentor?.id}/mentoring/ongoing/`}
          >
            <Input
              style={{ cursor: 'pointer' }}
              value={
                mentorship?.mentor?.firstName +
                ' ' +
                mentorship?.mentor?.lastName
              }
              icon={<ArrowRightIcon />}
              iconSide="right"
              readOnly
            />
          </Link>
        </InputContainer>
        <InputContainer>
          <ChangeReferee mentorship={mentorship} />
        </InputContainer>
      </Info>
    </div>
  );
};

export default InterruptionRequest;
